import type { SerializedExcalidrawElement } from "./domain.js";

/**
 * The byte-exact scene text the server hashes (SHA-256) for its operation
 * log and for `canvas:verify`. Shared so an editor can hash the canonical
 * scene it believes it holds and compare it with the server's, which is how
 * silent divergence is detected instead of lingering until the next join.
 */

type SceneElement = SerializedExcalidrawElement;
type IndexedElement = SceneElement & { index: string };

const hasFractionalIndex = (element: SceneElement): element is IndexedElement =>
  typeof element.index === "string";

/** Mirrors Excalidraw `orderByFractionalIndex`; legacy scenes keep merge order. */
export const orderScene = (scene: SceneElement[]): SceneElement[] => {
  if (!scene.every(hasFractionalIndex)) {
    return scene;
  }
  return [...(scene as IndexedElement[])].sort((a, b) => {
    if (a.index !== b.index) {
      return a.index < b.index ? -1 : 1;
    }
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
};

/**
 * Sorted-key JSON, so hashes survive PostgreSQL JSONB key reordering.
 * Undefined members are skipped as `JSON.stringify` would: data that came
 * over the wire never has them, an editor's in-memory copy may.
 */
export const canonicalJson = (value: unknown): string => {
  if (Array.isArray(value)) {
    return `[${value.map(canonicalJson).join(",")}]`;
  }
  if (value !== null && typeof value === "object") {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record)
      .filter((key) => record[key] !== undefined)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
};

/**
 * A short fingerprint of one element's canonical text (cyrb53, hex), for
 * `canvas:repair`: an editor whose scene hash disagreed lists one per element
 * so the server can send back only the elements that differ. Not a security
 * hash: a collision only means one element stays wrong until the next verify
 * round notices and the editor falls back to a full rejoin.
 */
export const elementDigest = (element: SceneElement): string => {
  const text = canonicalJson(element);
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index);
    h1 = Math.imul(h1 ^ code, 2654435761);
    h2 = Math.imul(h2 ^ code, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16);
};
